import React, { useRef } from "react";
import { useReveal } from "../hooks/useReveal";

const PARTNERS_COPY = {
  pt: {
    eyebrow: "Os nossos",
    title: "Parceiros",
    visitLabel: (name) => `Visitar site de ${name}`,
  },
  en: {
    eyebrow: "Our",
    title: "Partners",
    visitLabel: (name) => `Visit ${name} website`,
  },
};

export default function Partners({ lang = "pt" }) {
  const bg = "#4CB1A1"; 
  const PARTNERS = [
    {
      name: "Optigest",
      logo: "/assets/optigest.png",
      url: "https://www.optigest.net/www/PT/",
      scale: 1.8,
    },
    {
      name: "Seguramos",
      logo: "/assets/seguramos.png",
      url: "https://seguramos.pt/",
      scale: 1.6,
    },
    {
      name: "Startup Portugal",
      logo: "/assets/startup.png",
      url: "https://startupportugal.com/",
      scale: 2.4,
    },
    {
      name: "Câmara.AI",
      logo: "/assets/camara.ai.png",
      url: "https://camara.ai/",
      scale: 2.2,
    },
    {
      name: "IP Brick",
      logo: "/assets/ip.png",
      url: "https://www.ipbrick.com/",
      scale: 2.2,
    },
    {
      name: "ANJE",
      logo: "/assets/anje.png",
      url: "https://www.anje.pt/",
      scale: 1.2,
    },
    {
      name: "i2S",
      logo: "/assets/i2s.png",
      url: "https://i2sbrokers.pt",
      scale: 1,
    },
  ];

  const copy = PARTNERS_COPY[lang] || PARTNERS_COPY.pt;

  const headingRef = useRef(null);
  const trackRef = useRef(null);
  useReveal(headingRef);
  useReveal(trackRef, { threshold: 0.2 });

  const tailCount = Math.min(3, PARTNERS.length);
  const marqueePartners = [...PARTNERS, ...PARTNERS.slice(0, tailCount)];
  const scrollDistance = (PARTNERS.length / marqueePartners.length) * 100;
  const scrollDuration = marqueePartners.length * 3.2;

  return (
    <section
      aria-labelledby="partners-title"
      className="relative w-full"
      style={{ backgroundColor: bg }}
    >
      {/* animação de scroll contínuo */}
      <style>{`
        @keyframes partners-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(calc(-1 * var(--scroll-distance, 50%))); }
        }
        .partners-track {
          animation: partners-scroll var(--scroll-duration, 22s) linear infinite;
          will-change: transform;
        }
        .partners-viewport:hover .partners-track,
        .partners-viewport:focus-within .partners-track {
          animation-play-state: paused;
        }
      `}</style>

      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 pt-8 sm:pt-10 pb-12 sm:pb-16">
        <div ref={headingRef} className="reveal text-center text-white">
          <p className="uppercase tracking-[0.24em] text-[11px] sm:text-xs opacity-90">
            {copy.eyebrow}
          </p>
          <h2
            id="partners-title"
            className="mt-1 text-3xl sm:text-4xl font-extrabold"
          >
            {copy.title}
          </h2>
        </div>

        {/* viewport */}
        <div className="partners-viewport relative mt-8 sm:mt-10 overflow-hidden">

          <div
            ref={trackRef}
            className="reveal partners-track flex items-center gap-20 sm:gap-28 lg:gap-32"
            style={{
              "--scroll-distance": `${scrollDistance}%`,
              "--scroll-duration": `${scrollDuration}s`,
            }}
          >
            {marqueePartners.map((partner, idx) => (
              <a
                key={`${partner.name}-${idx}`}
                href={partner.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={copy.visitLabel(partner.name)}
                className="group relative flex h-[clamp(3rem,6.5vw,4.5rem)] w-[clamp(7rem,14vw,10.5rem)] shrink-0 items-center justify-center px-4 py-2 outline-none transition-transform duration-500 ease-out focus-visible:ring-2 focus-visible:ring-white/75"
              >
                <img
                  src={partner.logo}
                  alt={partner.name}
                  className="h-full w-full transform object-contain transition-transform duration-500 ease-out"
                  style={{ transform: `scale(${partner.scale ?? 1})` }}
                  loading="lazy"
                />
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
